import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, Badge, Row, Col, Spinner } from "reactstrap";
import { Table } from "antd";
import dayjs from "dayjs";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../components/Component";
import RecordPaymentModal from "./RecordPaymentModal";
import { generateInvoicePdf } from "./invoicePdfGenerator";
import customerAdminApis from "../../api/customer-admin/customerAdmin";
import { formatCurrency } from "../../utils/Utils";

const statusColorMap: Record<string, string> = {
  Sent: "primary",
  Paid: "success",
  "Partially Paid": "warning",
  Overdue: "danger",
  Cancelled: "secondary",
  Pending: "info",
};

const CustomerInvoices = () => {
  const { sm, updateSm } = useOutletContext<any>();
  const [loading, setLoading] = useState(true);

  // List state
  const [invoices, setInvoices] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Detail state
  const [viewMode, setViewMode] = useState<"list" | "detail">("list");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [invoiceDetail, setInvoiceDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Record payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser && currentUser.genericRole !== "admin") {
      navigate(`${process.env.PUBLIC_URL}/dashboard`);
      return;
    }
    fetchInvoices(1, 10);
  }, [currentUser]);

  const fetchInvoices = async (page: number, pageSize: number) => {
    setLoading(true);
    try {
      const response = await customerAdminApis.fetchInvoices({
        page,
        pageSize,
      });
      if (response?.status && response?.data) {
        setInvoices(response.data.data || []);
        setPagination({
          current: response.data.currentPage || page,
          pageSize: response.data.pageSize || pageSize,
          total: response.data.totalRecords || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceDetail = async (invoiceId: number) => {
    setDetailLoading(true);
    try {
      const response = await customerAdminApis.fetchInvoiceById(invoiceId);
      if (response?.status && response?.data) {
        setInvoiceDetail(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch invoice detail:", error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleInvoiceClick = (invoiceId: number) => {
    setSelectedInvoiceId(invoiceId);
    setViewMode("detail");
    fetchInvoiceDetail(invoiceId);
  };

  const handleBackToList = () => {
    setViewMode("list");
    setSelectedInvoiceId(null);
    setInvoiceDetail(null);
  };

  const handleTableChange = (pag: any) => {
    fetchInvoices(pag.current, pag.pageSize);
  };

  const handlePaymentSuccess = () => {
    if (selectedInvoiceId) {
      fetchInvoiceDetail(selectedInvoiceId);
    }
    fetchInvoices(pagination.current, pagination.pageSize);
  };

  const listColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_: any, __: any, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (text: string, record: any) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleInvoiceClick(record.invoiceId);
          }}
          className="text-primary fw-bold"
        >
          {text}
        </a>
      ),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (date: string) => (date ? dayjs(date).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (date: string) => (date ? dayjs(date).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Badge color={statusColorMap[status] || "secondary"}>{status}</Badge>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status: string) => (
        <Badge color={statusColorMap[status] || "secondary"}>{status}</Badge>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right" as const,
      render: (val: number) => formatCurrency(val, "INR"),
    },
    {
      title: "Paid",
      dataIndex: "paidAmount",
      key: "paidAmount",
      align: "right" as const,
      render: (val: number) => formatCurrency(val, "INR"),
    },
    {
      title: "Balance",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      align: "right" as const,
      render: (val: number) => formatCurrency(val, "INR"),
    },
  ];

  const lineItemColumns = [
    {
      title: "#",
      dataIndex: "lineNumber",
      key: "lineNumber",
      width: 50,
    },
    {
      title: "Module",
      dataIndex: "moduleName",
      key: "moduleName",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right" as const,
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      align: "right" as const,
      render: (val: number) => formatCurrency(val, "INR"),
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right" as const,
      render: (val: number) => formatCurrency(val, "INR"),
    },
  ];

  const renderDetailView = () => {
    if (detailLoading) {
      return (
        <div className="d-flex justify-content-center py-5">
          <Spinner color="primary" />
        </div>
      );
    }

    if (!invoiceDetail) return null;

    const inv = invoiceDetail;

    return (
      <>
        <div className="d-flex justify-content-between align-items-center pb-3">
          <Button
            color="light"
            className="btn-dim d-inline-flex align-items-center"
            onClick={handleBackToList}
          >
            <Icon name="arrow-left" />
            <span className="ms-1">Back to Invoices</span>
          </Button>
          <div className="d-flex gap-3">
            <Button
              color="light"
              className="btn-dim d-inline-flex align-items-center py-3"
              onClick={() => generateInvoicePdf(inv)}
            >
              <Icon name="download" />
              <span className="ms-1">Download PDF</span>
            </Button>
            {inv.balanceAmount > 0 && !['Cancelled', 'Failed'].includes(inv.status) && (
              <Button
                color="primary"
                className="btn-dim d-inline-flex align-items-center py-3"
                onClick={() => setShowPaymentModal(true)}
              >
                <Icon name="wallet-in" />
                <span className="ms-1">Record Payment</span>
              </Button>
            )}
          </div>
        </div>

        {/* Invoice Header */}
        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              <Row className="gy-3">
                <Col sm="4">
                  <span className="sub-text">Invoice Number</span>
                  <span className="caption-text fw-bold">{inv.invoiceNumber}</span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Invoice Date</span>
                  <span className="caption-text">
                    {dayjs(inv.invoiceDate).format("DD-MMM-YYYY")}
                  </span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Due Date</span>
                  <span className="caption-text">
                    {dayjs(inv.dueDate).format("DD-MMM-YYYY")}
                  </span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Billing Period</span>
                  <span className="caption-text">{inv.billingPeriod || "-"}</span>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Status</span>
                  <Badge color={statusColorMap[inv.status] || "secondary"}>
                    {inv.status}
                  </Badge>
                </Col>
                <Col sm="4">
                  <span className="sub-text">Payment Status</span>
                  <Badge color={statusColorMap[inv.paymentStatus] || "secondary"}>
                    {inv.paymentStatus}
                  </Badge>
                </Col>
                {inv.currencyCode && (
                  <Col sm="4">
                    <span className="sub-text">Currency</span>
                    <span className="caption-text">{inv.currencyCode}</span>
                  </Col>
                )}
                {inv.customerName && (
                  <Col sm="4">
                    <span className="sub-text">Customer</span>
                    <span className="caption-text">{inv.customerName}</span>
                  </Col>
                )}
                {inv.customerGstin && (
                  <Col sm="4">
                    <span className="sub-text">GSTIN</span>
                    <span className="caption-text">{inv.customerGstin}</span>
                  </Col>
                )}
                {inv.customerPan && (
                  <Col sm="4">
                    <span className="sub-text">PAN</span>
                    <span className="caption-text">{inv.customerPan}</span>
                  </Col>
                )}
                {inv.placeOfSupply && (
                  <Col sm="4">
                    <span className="sub-text">Place of Supply</span>
                    <span className="caption-text">{inv.placeOfSupply}</span>
                  </Col>
                )}
              </Row>
            </div>
          </Card>
        </Block>

        {/* Line Items */}
        {inv.invoiceLines && inv.invoiceLines.length > 0 && (
          <Block>
            <Card className="card-bordered">
              <div className="card-inner">
                <h6 className="overline-title-alt mb-3">Line Items</h6>
                <Table
                  columns={lineItemColumns}
                  dataSource={inv.invoiceLines}
                  rowKey="invoiceLineId"
                  pagination={false}
                  size="small"
                />
              </div>
            </Card>
          </Block>
        )}

        {/* Invoice Summary */}
        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              <h6 className="overline-title-alt mb-3">Invoice Summary</h6>
              <div style={{ maxWidth: 400, marginLeft: "auto" }}>
                <div className="d-flex justify-content-between py-1">
                  <span className="sub-text">Subtotal</span>
                  <span>{formatCurrency(inv.subtotalAmount, "INR")}</span>
                </div>
                {inv.couponAmount > 0 && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="sub-text">Coupon ({inv.couponCode})</span>
                    <span className="text-success">-{formatCurrency(inv.couponAmount, "INR")}</span>
                  </div>
                )}
                {inv.manualDiscountAmount > 0 && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="sub-text">Discount ({inv.manualDiscountType})</span>
                    <span className="text-success">-{formatCurrency(inv.manualDiscountAmount, "INR")}</span>
                  </div>
                )}
                {inv.totalDiscountAmount > 0 && (inv.couponAmount > 0 || inv.manualDiscountAmount > 0) && inv.totalDiscountAmount !== inv.couponAmount && inv.totalDiscountAmount !== inv.manualDiscountAmount && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="sub-text">Total Discount</span>
                    <span className="text-success">-{formatCurrency(inv.totalDiscountAmount, "INR")}</span>
                  </div>
                )}
                {inv.advanceAdjusted > 0 && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="sub-text">Advance Adjusted</span>
                    <span className="text-info">-{formatCurrency(inv.advanceAdjusted, "INR")}</span>
                  </div>
                )}
                <hr className="my-1" />
                <div className="d-flex justify-content-between py-1">
                  <span className="sub-text">Taxable Amount</span>
                  <span>{formatCurrency(inv.taxableAmount, "INR")}</span>
                </div>
                {inv.cgstAmount > 0 && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="sub-text">CGST</span>
                    <span>{formatCurrency(inv.cgstAmount, "INR")}</span>
                  </div>
                )}
                {inv.sgstAmount > 0 && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="sub-text">SGST</span>
                    <span>{formatCurrency(inv.sgstAmount, "INR")}</span>
                  </div>
                )}
                {inv.igstAmount > 0 && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="sub-text">IGST</span>
                    <span>{formatCurrency(inv.igstAmount, "INR")}</span>
                  </div>
                )}
                <hr className="my-1" />
                <div className="d-flex justify-content-between py-1 fw-bold">
                  <span>Total Amount</span>
                  <span>{formatCurrency(inv.totalAmount, "INR")}</span>
                </div>
                {inv.netPayableAmount > 0 && inv.netPayableAmount !== inv.totalAmount && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="sub-text">Net Payable</span>
                    <span>{formatCurrency(inv.netPayableAmount, "INR")}</span>
                  </div>
                )}
                <div className="d-flex justify-content-between py-1">
                  <span className="sub-text">Paid Amount</span>
                  <span className="text-success">{formatCurrency(inv.paidAmount, "INR")}</span>
                </div>
                <div className="d-flex justify-content-between py-1 fw-bold">
                  <span>Balance Due</span>
                  <span className="text-danger">{formatCurrency(inv.balanceAmount, "INR")}</span>
                </div>
              </div>
            </div>
          </Card>
        </Block>

        {/* Notes */}
        {inv.notes && (
          <Block>
            <Card className="card-bordered">
              <div className="card-inner">
                <h6 className="overline-title-alt mb-2">Notes</h6>
                <p className="text-soft">{inv.notes}</p>
              </div>
            </Card>
          </Block>
        )}

        {/* Advance Adjustments */}
        {inv.advanceAdjustments && inv.advanceAdjustments.length > 0 && (
          <Block>
            <Card className="card-bordered">
              <div className="card-inner">
                <h6 className="overline-title-alt mb-3">Advance Adjustments</h6>
                <Table
                  columns={[
                    { title: "Advance Ref", dataIndex: "advanceReferenceNo", key: "advanceReferenceNo" },
                    {
                      title: "Amount",
                      dataIndex: "adjustedAmount",
                      key: "adjustedAmount",
                      align: "right" as const,
                      render: (val: number) => formatCurrency(val, "INR"),
                    },
                    {
                      title: "Date",
                      dataIndex: "adjustmentDate",
                      key: "adjustmentDate",
                      render: (date: string) => (date ? dayjs(date).format("DD-MMM-YYYY") : "-"),
                    },
                  ]}
                  dataSource={inv.advanceAdjustments}
                  rowKey="adjustmentId"
                  pagination={false}
                  size="small"
                />
              </div>
            </Card>
          </Block>
        )}

        {/* Record Payment Modal */}
        {showPaymentModal && (
          <RecordPaymentModal
            isOpen={showPaymentModal}
            toggle={() => setShowPaymentModal(false)}
            invoiceId={inv.invoiceId}
            invoiceNumber={inv.invoiceNumber}
            balanceAmount={inv.balanceAmount}
            onSuccess={handlePaymentSuccess}
          />
        )}
      </>
    );
  };

  return (
    <React.Fragment>
      <Head title="Invoices" />
      {viewMode === "list" ? (
        <>
          <BlockHead size="lg">
            <BlockBetween>
              <BlockHeadContent>
                <BlockTitle tag="h4">Invoices</BlockTitle>
                <BlockDes>
                  <p>View and manage your subscription invoices.</p>
                </BlockDes>
              </BlockHeadContent>
              <BlockHeadContent className="align-self-start d-lg-none">
                <Button
                  className={`toggle btn btn-icon btn-trigger mt-n1 ${sm ? "active" : ""}`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="menu-alt-r" />
                </Button>
              </BlockHeadContent>
            </BlockBetween>
          </BlockHead>

          <Block>
            <Table
              columns={listColumns}
              dataSource={invoices}
              rowKey="invoiceId"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              onChange={handleTableChange}
              size="middle"
              scroll={{ x: 900 }}
            />
          </Block>
        </>
      ) : (
        renderDetailView()
      )}
    </React.Fragment>
  );
};

export default CustomerInvoices;
